// Central content + config. Everything editable in one place for easy owner updates.

export const BUSINESS = {
  name: "Cozy Pet Care",
  tagline: "Personal Pet Care When You Can't Be There",
  serviceArea: "Katy & Fulshear, Texas",
  phoneDisplay: "(832) 212-8045",
  phoneRaw: "+18322128045",
  email: "cozyadmin@netvolumes.com",
  social: {
    facebook: "https://facebook.com/",
    instagram: "https://instagram.com/",
    google: "https://business.google.com/",
  },
};

export const IMAGES = {
  heroDog: "/gallery/p48.jpg?v=3",
  heroCat: "/gallery/w16.jpg?v=3",
  dogBoarding: "/gallery/p50.jpg?v=3",
  catBoarding: "/gallery/w22.jpg?v=3",
  inHome: "/gallery/m26.jpg?v=3",
  companionship: "https://images.pexels.com/photos/16395150/pexels-photo-16395150.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  care: "https://images.unsplash.com/photo-1601931935934-17c3717239ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&ixlib=rb-4.1.0",
  about: "https://images.pexels.com/photos/6643659/pexels-photo-6643659.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  shop: "https://images.unsplash.com/photo-1580597095981-8d21aa8cdfed?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&ixlib=rb-4.1.0",
};

export const TRUST_POINTS = [
  { icon: "Heart", text: "Personal, attentive care" },
  { icon: "Home", text: "Private home-style boarding" },
  { icon: "PawPrint", text: "Dog and cat care available" },
  { icon: "Utensils", text: "Feeding instructions followed" },
  { icon: "Droplets", text: "Fresh water provided" },
  { icon: "Footprints", text: "Exercise, potty and play breaks" },
  { icon: "Camera", text: "Daily photo updates" },
  { icon: "MapPinned", text: "In-home visits available" },
  { icon: "Tag", text: "Clear daily pricing" },
  { icon: "ShieldCheck", text: "Local and dependable service" },
];

export const SERVICES = [
  {
    id: "dog-boarding",
    title: "Private Dog Boarding",
    price: 45,
    unit: "per day",
    note: "Small dogs only — medium to large dogs are cared for by our in-home service only.",
    image: IMAGES.dogBoarding,
    description:
      "Small dogs stay in a private home-style environment and receive feeding, fresh water, bathroom breaks, supervision, and personal attention according to the owner's instructions.",
  },
  {
    id: "cat-boarding",
    title: "Private Cat Boarding",
    price: 25,
    unit: "per day",
    image: IMAGES.catBoarding,
    description:
      "Cats receive a quiet, secure and comfortable area with feeding, fresh water, litter care and personal attention according to the owner's instructions.",
  },
  {
    id: "in-home-visit",
    title: "In-Home Pet Visit",
    price: 25,
    unit: "per visit",
    image: IMAGES.inHome,
    description:
      "We travel to the client's home to feed your pet, provide fresh water, let dogs outside for a bathroom and activity break, and return indoors or to its kennel according to the owner's written instructions.",
  },
];

// Base options for the estimator
export const BASE_SERVICES = [
  { id: "dog-boarding", label: "Private Dog Boarding", price: 45, unit: "day", countLabel: "Number of days" },
  { id: "cat-boarding", label: "Private Cat Boarding", price: 25, unit: "day", countLabel: "Number of days" },
  { id: "in-home-visit", label: "In-Home Pet Visit", price: 25, unit: "visit", countLabel: "Number of visits" },
];

// Add-on groups. price=null means "Quote required".
export const ADDON_GROUPS = [
  {
    title: "Exercise & Personal Attention",
    icon: "Footprints",
    items: [
      { id: "extra-play", label: "Extra supervised exercise or play session (20 min)", price: 10, unit: "per session" },
      { id: "walk-short", label: "Individual neighborhood dog walk (20–30 min)", price: 15, unit: "per walk" },
      { id: "walk-long", label: "Extended dog walk (45–60 min)", price: 25, unit: "per walk" },
      { id: "cuddle", label: "One-on-one comfort or cuddle session (20 min)", price: 10, unit: "per session" },
    ],
  },
  {
    title: "Medication Services",
    icon: "Pill",
    note: "Medication must be supplied in its original labeled container with complete written instructions. Medication services require advance discussion and approval. We may decline any medication procedure that cannot be performed safely.",
    items: [
      { id: "med-oral", label: "Oral medication (tablet, capsule, or liquid)", price: 5, unit: "per administration" },
      { id: "med-drops", label: "Eye or ear drops", price: 7, unit: "per administration" },
      { id: "med-multiple", label: "Multiple medications during one session", price: 10, unit: "per session" },
      { id: "med-injection", label: "Injection or insulin administration", price: null, unit: "quote required" },
    ],
  },
  {
    title: "Grooming & Hygiene",
    icon: "Scissors",
    items: [
      { id: "brush-basic", label: "Basic brushing (up to 15 min)", price: 10, unit: "per session" },
      { id: "brush-extended", label: "Extended brushing or de-shedding (up to 30 min)", price: 18, unit: "per session" },
      { id: "bath-small", label: "Small dog bath (up to 25 lb)", price: 25, unit: "per bath" },
      { id: "bath-medium", label: "Medium dog bath (26–50 lb)", price: 35, unit: "per bath" },
      { id: "bath-large", label: "Large dog bath (51–80 lb)", price: 45, unit: "per bath" },
      { id: "bath-xl", label: "Extra-large dog bath (over 80 lb)", price: 55, unit: "per bath" },
      { id: "nail-dog", label: "Dog nail clipping", price: 12, unit: "per pet" },
      { id: "nail-cat", label: "Cat nail clipping", price: 15, unit: "per pet" },
      { id: "teeth", label: "Teeth brushing", price: 7, unit: "per session" },
      { id: "litter-extra", label: "Additional litter-box cleaning beyond daily care", price: 5, unit: "per cleaning" },
    ],
  },
  {
    title: "Transportation & Updates",
    icon: "Car",
    items: [
      { id: "photo-update", label: "Photo and written update", price: 5, unit: "per update" },
      { id: "pickup", label: "Pet pickup within local service area", price: 15, unit: "one-time" },
      { id: "return", label: "Pet return within local service area", price: 15, unit: "one-time" },
    ],
  },
  {
    title: "Additional Care Charges",
    icon: "Plus",
    items: [
      { id: "extra-dog", label: "Additional dog from the same household", price: 25, unit: "per day" },
      { id: "extra-cat", label: "Additional cat from the same household", price: 20, unit: "per day" },
      { id: "puppy", label: "Puppy care (dog under one year old)", price: 10, unit: "per day" },
      { id: "holiday", label: "Holiday care surcharge", price: 10, unit: "per pet, per day" },
      { id: "late-pickup", label: "Late pickup / extended care (2–8 hours)", price: 22.5, unit: "one-time" },
      { id: "extended-8", label: "Extended care exceeding 8 hours", price: 45, unit: "one-time" },
    ],
  },
];

export const CARE_STEPS = [
  { icon: "Utensils", title: "Following each owner's feeding schedule", text: "We feed your pet the food you provide, in the amounts and at the times you specify, keeping their routine familiar and stress-free." },
  { icon: "Droplets", title: "Maintaining access to clean water", text: "Fresh, clean water is always available, and bowls are refreshed throughout the day." },
  { icon: "Footprints", title: "Bathroom and exercise breaks", text: "Dogs receive regular potty breaks and activity time appropriate to their age, energy and comfort level." },
  { icon: "Eye", title: "Safe supervision", text: "Pets are supervised attentively in a calm, home-style setting, never lost in a crowd of animals." },
  { icon: "Pill", title: "Medication only when discussed and approved in advance", text: "Any medication is given only after we discuss it with you and agree it can be handled safely." },
  { icon: "Clock", title: "Respecting the pet's normal routine", text: "We follow your pet's usual schedule as closely as possible to keep them relaxed and comfortable." },
  { icon: "Split", title: "Pets are always kept separate", text: "Every pet has their own private space and is always kept separate from other pets, keeping them safe at all times and preventing accidents." },
  { icon: "PhoneCall", title: "Contacting the owner if a concern develops", text: "If something seems off, we reach out to you promptly using the contact details you provide." },
  { icon: "Camera", title: "Daily pet photographs and updates", text: "We take photos of your pet and send them to you every day so you can see how they're doing." },
  { icon: "Sparkles", title: "Cleaning food bowls, litter areas and pet spaces", text: "We keep bowls, litter areas and resting spaces clean and tidy as part of daily care." },
];

export const FAQS = [
  { q: "How is a day of boarding calculated?", a: "Boarding is charged per day, not per overnight stay. Drop-off and pick-up days are each charged as a full day. We'll confirm exactly how many days are counted for your pet's stay." },
  { q: "What should I bring for boarding?", a: "Pets should be brought in their own carriers or on a leash. When dropping off, please do not allow your dogs to go potty on our front lawn, as this encourages other dogs to do the same and we'd like to avoid that. When we receive a dog we immediately take them out so they can relieve themselves before their stay begins; we have a designated area behind our garage for that purpose, as well as the walking trail in front of our home. Please also bring cat litter, food, water bowls, poopie bags, brush, and any items you feel are necessary such as scratching posts, chew sticks and snacks." },
  { q: "Should I bring my pet's regular food?", a: "Yes. Keeping your pet on their regular food helps avoid stomach upset and keeps their routine consistent. Please avoid changing food or litter brands at the time of their stay, as this often causes pets more stress and can lead to loose stools, which can be a problem." },
  { q: "Can you follow special feeding instructions?", a: "Absolutely. We follow the feeding schedule, portions and instructions you provide." },
  { q: "Can you give medications?", a: "Medication is only given after advance discussion and approval, and must be supplied with written instructions. We may decline any procedure that cannot be performed safely." },
  { q: "Will I receive updates or pictures?", a: "Yes, photos are taken and sent to you daily." },
  { q: "Do you accept dogs and cats?", a: "Yes, we care for both dogs and cats." },
  { q: "Are pets boarded in a private home?", a: "Yes. Boarding takes place in a private, home-style environment rather than a large commercial kennel." },
  { q: "What happens during an in-home visit?", a: "We travel to your home to feed your pet, provide fresh water, give dogs a bathroom and activity break, and return the pet indoors or to its kennel per your written instructions." },
  { q: "How long is an in-home visit?", a: "Essential care is generally 20-25 minutes based on the requested instructions and number of pets." },
  { q: "Do you provide overnight house sitting?", a: "No." },
  { q: "Is my reservation automatically confirmed?", a: "No. Contacting us by text or email is only a request. Your reservation is not confirmed until we review and discuss the details and availability with you." },
  { q: "What is the cancellation policy?", a: "Please contact us as early as possible if you need to cancel or change your dates. See full policy details in the links provided at the bottom of this page." },
  { q: "What vaccinations or records are required?", a: "Any records you feel are necessary to provide, you may do so. It is left to your discretion whether it is information we need for your pet's stay." },
  { q: "What happens in an emergency?", a: "You are our first contact. If you have provided us with your pet's veterinarian info. and it becomes necessary to take them to the vet, we will follow the instructions you have given. Generally this is not needed, but should an emergency happen it's good to have this information ahead of time." },
  { q: "What areas do you serve?", a: `We serve ${BUSINESS.serviceArea} and the surrounding local area. Contact us to confirm we cover your location.` },
];

export const SHOP_CATEGORIES = [
  { name: "Pet clothing & sweaters", icon: "Shirt" },
  { name: "Grooming brushes", icon: "Brush" },
  { name: "Pet-hair removal tools", icon: "Sparkles" },
  { name: "Collapsible travel bowls", icon: "Utensils" },
  { name: "Portable dog water bottles", icon: "Droplets" },
  { name: "Slow-feeder bowls", icon: "Soup" },
  { name: "Lick mats", icon: "Layers" },
  { name: "Paw-cleaning cups", icon: "Footprints" },
  { name: "Training accessories", icon: "GraduationCap" },
  { name: "Cat toys", icon: "Cat" },
  { name: "Dog toys", icon: "Dog" },
  { name: "Feeding mats", icon: "Grid2x2" },
  { name: "Travel accessories", icon: "Car" },
];

export const GALLERY = {
  Dogs: [
    { src: "/gallery/p46.jpg?v=3", desc: "terrier at home", w: 962, h: 718 },
    { src: "/gallery/p49.jpg?v=3", desc: "puppy in a plaid harness", w: 818, h: 812 },
    { src: "/gallery/p48.jpg?v=3", desc: "chocolate doodle on the sofa", w: 597, h: 685 },
    { src: "/gallery/w21.jpg?v=3", desc: "shih-tzu waiting for a treat", w: 690, h: 743 },
    { src: "/gallery/m26.jpg?v=3", desc: "beagle mix on the patio", w: 791, h: 756 },
    { src: "/gallery/n39.jpg?v=3", desc: "dachshund at home", w: 688, h: 827 },
  ],
  Cats: [
    { src: "/gallery/w16.jpg?v=3", desc: "orange tabby on the cat tree", w: 464, h: 538 },
    { src: "/gallery/w22.jpg?v=3", desc: "brown tabby lounging on the carpet", w: 801, h: 741 },
    { src: "/gallery/m24.jpg?v=3", desc: "black cat at home", w: 554, h: 635 },
    { src: "/gallery/x34.jpg?v=3", desc: "tabby cat by the window", w: 684, h: 769 },
    { src: "/gallery/n30.jpg?v=3", desc: "tuxedo cat on the carpet", w: 1002, h: 954 },
    { src: "/gallery/p52.jpg?v=3", desc: "black and white longhair cat", w: 756, h: 795 },
    { src: "/gallery/n38.jpg?v=3", desc: "tabby cat perched by the sink", w: 691, h: 601 },
    { src: "/gallery/cat-grey.jpg?v=3", desc: "long-haired tabby cat", w: 1357, h: 1368 },
  ],
  Boarding: [
    { src: "/gallery/p50.jpg?v=3", desc: "doodle resting indoors", w: 938, h: 785 },
    { src: "/gallery/m28.jpg?v=3", desc: "shepherd mix relaxing with a chew", w: 929, h: 864 },
    { src: "/gallery/dog-black.webp?v=3", desc: "orange cat napping on a blanket", w: 2000, h: 1667 },
    { src: "/gallery/p45.jpg?v=3", desc: "orange cat curled on a blanket", w: 839, h: 733 },
  ],
  "Play & Exercise": [
    { src: "/gallery/p44.jpg?v=3", desc: "French bulldog on a walk", w: 741, h: 748 },
    { src: "/gallery/p51.jpg?v=3", desc: "dog playing with a tennis ball", w: 813, h: 800 },
    { src: "/gallery/n31.jpg?v=3", desc: "two dogs playing in the yard", w: 950, h: 1552 },
    { src: "/gallery/n35.jpg?v=3", desc: "terrier exploring the yard", w: 765, h: 859 },
    { src: "/gallery/n42.jpg?v=3", desc: "doodle on a neighborhood walk", w: 708, h: 579 },
    { src: "/gallery/m12.jpg?v=3", desc: "Westie on a walk", w: 718, h: 693 },
    { src: "/gallery/w17.jpg?v=3", desc: "border collie in the grass", w: 549, h: 565 },
    { src: "/gallery/dog-lab-puppy.jpg?v=3", desc: "lab puppy with a tennis ball", w: 763, h: 799 },
    { src: "/gallery/cat-orange.jpg?v=3", desc: "dog on a walk in the grass", w: 980, h: 751 },
  ],
  "Feeding & Daily Care": [
    { src: "/gallery/m13.jpg?v=3", desc: "orange tabby getting head scratches", w: 802, h: 686 },
    { src: "/gallery/m25.jpg?v=3", desc: "orange tabby at grooming time", w: 891, h: 990 },
    { src: "/gallery/x27.jpg?v=3", desc: "cat enjoying a fresh meal", w: 734, h: 567 },
    { src: "/gallery/x33.jpg?v=3", desc: "cat by the food shelf", w: 831, h: 1202 },
    { src: "/gallery/m29.jpg?v=3", desc: "kitten exploring at home", w: 785, h: 588 },
  ],
};

export const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    updated: "Effective Date: August 3, 2026",
    blocks: [
      { t: "p", x: "Cozy Pet Care respects your privacy. This Privacy Policy explains the basic information we collect and how we use it." },
      { t: "h", x: "Information We Collect" },
      { t: "p", x: "We may collect:" },
      { t: "ul", x: [
        "Your name, phone number, email address, and home address",
        "Your pet's name, age, breed, health information, medications, behavior, and care instructions",
        "Emergency contact and veterinarian information",
        "Reservation, service, and payment information",
        "Messages, forms, photographs, and other information you provide",
      ] },
      { t: "h", x: "How We Use Information" },
      { t: "p", x: "We use this information to:" },
      { t: "ul", x: [
        "Schedule and provide pet-care services",
        "Contact you about your reservation or pet",
        "Follow your pet's care instructions",
        "Respond to an illness, injury, or emergency",
        "Process payments",
        "Maintain business and service records",
        "Improve our services and website",
      ] },
      { t: "h", x: "Sharing Information" },
      { t: "p", x: "We do not sell your personal information." },
      { t: "p", x: "We may share necessary information with:" },
      { t: "ul", x: [
        "Veterinarians or emergency animal hospitals",
        "Payment-processing companies",
        "Website, scheduling, communication, or business-service providers",
        "Authorities when required by law",
        "Another person when necessary to protect a pet, person, or property",
      ] },
      { t: "h", x: "Photographs" },
      { t: "p", x: "By using Cozy Pet Care's services, you grant permission for Cozy Pet Care to photograph your pet for the primary purpose of keeping you informed of your pet's well being. These photographs may be used for advertising, our website, or social media. You may withdraw permission for future public use by selecting the deny-public-use option in our Pet Care Agreement." },
      { t: "h", x: "Security" },
      { t: "p", x: "We take reasonable steps to protect the information entrusted to us. However, no electronic storage or transmission system can be guaranteed to be completely secure." },
      { t: "h", x: "Your Choices" },
      { t: "p", x: "You may ask us to review, correct, or delete personal information that we maintain, subject to information we must retain for legal, payment, insurance, or business-record purposes." },
      { t: "h", x: "Contact Us" },
      { t: "p", x: "For privacy questions, contact:" },
      { t: "contact" },
    ],
  },
  terms: {
    title: "Terms and Conditions",
    updated: "Effective Date: August 3, 2026",
    blocks: [
      { t: "p", x: "By requesting or purchasing services from Cozy Pet Care, you agree to these Terms and Conditions." },
      { t: "h", x: "Services" },
      { t: "p", x: "Cozy Pet Care provides pet boarding, home visits, feeding, exercise, walks, medication assistance, grooming-related add-ons, and other agreed pet-care services." },
      { t: "p", x: "The exact services, dates, prices, and instructions will be confirmed before care begins." },
      { t: "h", x: "Reservations" },
      { t: "p", x: "A reservation is not confirmed until:" },
      { t: "ul", x: [
        "Cozy Pet Care accepts the reservation;",
        "Required information and forms are completed; and",
        "Any required deposit or payment is received.",
      ] },
      { t: "p", x: "Services are subject to availability." },
      { t: "h", x: "Accurate Information" },
      { t: "p", x: "The pet owner must provide complete and accurate information about each pet, including:" },
      { t: "ul", x: [
        "Health conditions", "Medications", "Allergies", "Vaccinations", "Aggressive behavior",
        "Bite history", "Escape behavior", "Anxiety or destructive behavior", "Special needs",
        "Contact with contagious animals or diseases",
      ] },
      { t: "p", x: "Cozy Pet Care may refuse or discontinue care when important information was withheld or when a pet presents an unreasonable safety or health risk." },
      { t: "h", x: "Vaccinations and Health" },
      { t: "p", x: "Pets must be reasonably healthy and free from known contagious illnesses, fleas, ticks, or other conditions that may endanger people or animals." },
      { t: "p", x: "The owner is responsible for maintaining vaccinations and preventive care appropriate for the pet. Proof may be requested." },
      { t: "h", x: "Payment" },
      { t: "p", x: "Prices will be provided before services begin. Payment is due according to the invoice or reservation confirmation." },
      { t: "p", x: "The owner is responsible for:" },
      { t: "ul", x: [
        "Agreed service charges", "Approved add-on services", "Extended-care charges", "Veterinary expenses",
        "Supplies purchased for the pet", "Property damage caused by the pet",
        "Other reasonable costs resulting from inaccurate or incomplete instructions",
      ] },
      { t: "h", x: "Personal Property" },
      { t: "p", x: "Cozy Pet Care will use reasonable care with leashes, carriers, bedding, toys, food containers, keys, and other belongings. We are not responsible for ordinary wear, pet damage, or loss of items unless caused by our intentional misconduct or gross negligence." },
      { t: "h", x: "Pet Behavior and Risks" },
      { t: "p", x: "Pet care involves natural and sometimes unpredictable risks. Animals may become ill, injured, frightened, destructive, aggressive, or attempt to escape even when reasonable care is provided." },
      { t: "p", x: "The owner accepts the ordinary risks associated with pet care and remains responsible for the pet's behavior, health, veterinary expenses, and damage caused by the pet, except to the extent directly caused by Cozy Pet Care's gross negligence or intentional misconduct." },
      { t: "h", x: "Service Changes" },
      { t: "p", x: "Cozy Pet Care may modify the manner in which care is provided when reasonably necessary for the pet's safety, health, weather conditions, household safety, or an emergency." },
      { t: "h", x: "Severe Weather and Emergencies" },
      { t: "p", x: "During severe weather, unsafe road conditions, power outages, natural disasters, or other circumstances beyond our reasonable control, services may be delayed, shortened, moved, or modified." },
      { t: "p", x: "We will make reasonable efforts to care for the pet and communicate with the owner, but human and animal safety will take priority." },
      { t: "h", x: "Abandoned or Unclaimed Pets" },
      { t: "p", x: "The owner must arrange for the pet to be picked up at the agreed time." },
      { t: "p", x: "When the owner cannot be reached and a pet remains in our care beyond the scheduled period, Cozy Pet Care may continue necessary care and charge the regular daily rate plus any related expenses." },
      { t: "p", x: "If a pet appears to have been abandoned, we may contact the owner's emergency contact, veterinarian, animal-control authority, or another appropriate organization." },
      { t: "h", x: "Limitation of Liability" },
      { t: "p", x: "To the extent permitted by law, Cozy Pet Care's financial responsibility for a claim will not exceed the amount paid for the specific service giving rise to that claim, except in cases involving gross negligence, intentional misconduct, or liability that cannot legally be limited." },
      { t: "h", x: "Changes to These Terms" },
      { t: "p", x: "We may update these Terms and Conditions. The version accepted when a reservation is confirmed will apply to that reservation." },
      { t: "h", x: "Contact" },
      { t: "contact" },
    ],
  },
  cancellation: {
    title: "Cancellation Policy",
    updated: "Effective Date: August 3, 2026",
    blocks: [
      { t: "p", x: "Because we reserve time and space specifically for each pet, advance notice is required for cancellations." },
      { t: "h", x: "Regular Reservations (if applicable)" },
      { t: "ul", x: [
        "Seven or more calendar days before service begins: Full refund or account credit.",
        "Three to six calendar days before service begins: 50% refund or full account credit.",
        "Less than three calendar days before service begins: Payment is nonrefundable.",
      ] },
      { t: "h", x: "Holiday and High-Demand Reservations" },
      { t: "p", x: "For holiday periods and other high-demand dates:" },
      { t: "ul", x: [
        "A deposit of 50% is required.",
        "The deposit is refundable when cancellation is received at least 14 calendar days before service begins.",
        "Cancellations made less than 14 calendar days before service begins are nonrefundable.",
      ] },
      { t: "p", x: "Holiday periods includes normally observed holidays by local banks and schools." },
      { t: "h", x: "Early Returns and Unused Services" },
      { t: "p", x: "No refund is provided when:" },
      { t: "ul", x: [
        "An owner returns early;",
        "A pet is picked up before the scheduled date;",
        "A home visit is no longer needed after the service period has begun; or",
        "Part of a reserved service is unused.",
      ] },
      { t: "p", x: "At our discretion, an account credit may be offered." },
      { t: "h", x: "Late Arrivals or Pickups" },
      { t: "p", x: "Late pickup or additional care may be charged at the normal daily or visit rate." },
      { t: "p", x: "Please contact us as soon as possible when travel plans change." },
      { t: "h", x: "Cancellation by Cozy Pet Care" },
      { t: "p", x: "If Cozy Pet Care must cancel and cannot provide a reasonable alternative, the owner will receive a refund for services not provided." },
      { t: "p", x: "We may cancel or discontinue services without refund when:" },
      { t: "ul", x: [
        "A pet presents an undisclosed health or safety risk;",
        "Required information was materially inaccurate;",
        "The owner fails to provide necessary food, medication, access, or instructions;",
        "Payment is not made as agreed; or",
        "Continuing care would place a person or animal in danger.",
      ] },
      { t: "p", x: "Extenuating circumstances may be considered individually." },
    ],
  },
  agreement: {
    title: "Pet Care Agreement",
    form: true,
    blocks: [
      { t: "h", x: "Owner Information" },
      { t: "field", x: "Owner's Name" },
      { t: "field", x: "Address" },
      { t: "field", x: "Phone" },
      { t: "field", x: "Email" },
      { t: "field", x: "Emergency Contact" },
      { t: "field", x: "Emergency Contact Phone" },
      { t: "h", x: "Pet Information" },
      { t: "field", x: "Pet's Name" },
      { t: "field", x: "Type / Breed" },
      { t: "field", x: "Age" },
      { t: "field", x: "Sex" },
      { t: "field", x: "Spayed or Neutered (Yes / No)" },
      { t: "field", x: "Veterinarian" },
      { t: "field", x: "Veterinarian Phone" },
      { t: "h", x: "Scheduled Care" },
      { t: "p", x: "Service Type:" },
      { t: "check", x: [
        "Private boarding", "Home visits", "Feeding and fresh water", "Potty breaks", "Walks or exercise",
        "Medication assistance", "Other",
      ] },
      { t: "field", x: "Service Dates" },
      { t: "field", x: "Drop-Off / Start Time" },
      { t: "field", x: "Pickup / End Time" },
      { t: "field", x: "Agreed Price" },
      { t: "sub", x: "Feeding Instructions" },
      { t: "lines", n: 3 },
      { t: "sub", x: "Medication Instructions" },
      { t: "p", x: "List medication name, amount, method, and schedule:" },
      { t: "lines", n: 3 },
      { t: "sub", x: "Behavior and Safety" },
      { t: "p", x: "Please identify any history of:" },
      { t: "check", x: [
        "Biting or aggression", "Resource guarding", "Fighting with animals", "Fear of people or animals",
        "Separation anxiety", "Escaping or climbing fences", "Destructive behavior", "Excessive barking", "Other",
      ] },
      { t: "p", x: "Please explain:" },
      { t: "lines", n: 2 },
      { t: "sub", x: "Health Information" },
      { t: "p", x: "List allergies, illnesses, injuries, disabilities, dietary restrictions, or special needs:" },
      { t: "lines", n: 3 },
      { t: "h", x: "Owner Agreements" },
      { t: "p", x: "I certify that:" },
      { t: "ol", x: [
        "I am the pet's owner or have authority to arrange the pet's care.",
        "The information I provided is complete and accurate.",
        "I have disclosed all known medical conditions, medications, aggressive behavior, bite history, escape behavior, and special needs.",
        "I authorize Cozy Pet Care to provide the services and follow the instructions listed in this agreement.",
        "I authorize reasonable adjustments when necessary for my pet's health or safety.",
        "I accept the ordinary risks associated with pet boarding, home visits, walks, transportation, interaction with people, and interaction with other animals.",
        "I am responsible for agreed charges, veterinary expenses, additional supplies, extended care, and damage caused by my pet.",
        "I have received and agree to Cozy Pet Care's Terms and Conditions and Cancellation Policy.",
        "I will remain reachable or provide an emergency contact who can make decisions during the service period.",
        "I will notify Cozy Pet Care promptly of changes to my pet's health, behavior, medications, or care instructions.",
      ] },
      { t: "h", x: "Photo Permission" },
      { t: "p", x: "Please select one:" },
      { t: "check", x: [
        "Cozy Pet Care may photograph my pet and use the photographs privately to provide me daily updates.",
        "I do not wish photographs of my pet to be sent to me privately.",
      ] },
      { t: "p", x: "Please select one:" },
      { t: "check", x: [
        "Cozy Pet Care may also use photographs of my pet on its website, social media, or advertising.",
        "I do not wish photographs of my pet to be used publicly.",
      ] },
      { t: "h", x: "Signature" },
      { t: "field", x: "Owner's Printed Name" },
      { t: "field", x: "Owner's Signature" },
      { t: "field", x: "Date" },
    ],
  },
  emergency: {
    title: "Emergency Care Authorization",
    form: true,
    blocks: [
      { t: "p", x: "This authorization applies when my pet is receiving services from Cozy Pet Care." },
      { t: "h", x: "Owner and Pet" },
      { t: "field", x: "Owner's Name" },
      { t: "field", x: "Pet's Name" },
      { t: "field", x: "Primary Phone" },
      { t: "field", x: "Secondary Phone" },
      { t: "field", x: "Emergency Contact" },
      { t: "field", x: "Emergency Contact Phone" },
      { t: "h", x: "Veterinary Information" },
      { t: "field", x: "Regular Veterinarian" },
      { t: "field", x: "Veterinarian Phone" },
      { t: "field", x: "Preferred Emergency Clinic" },
      { t: "field", x: "Emergency Clinic Phone" },
      { t: "field", x: "Pet Insurance Company" },
      { t: "field", x: "Policy Number" },
      { t: "h", x: "Emergency Authorization" },
      { t: "p", x: "If my pet becomes ill, is injured, escapes, or experiences another emergency, I authorize Cozy Pet Care to:" },
      { t: "ol", x: [
        "Attempt to contact me and my emergency contact;",
        "Contact my regular veterinarian or an emergency veterinary facility;",
        "Transport or arrange transportation for my pet;",
        "Share relevant health and care information with veterinary personnel; and",
        "Authorize examination, diagnostic testing, stabilization, pain relief, medication, and other treatment reasonably considered necessary when I cannot be reached promptly.",
      ] },
      { t: "p", x: "Emergency veterinary authorization forms commonly specify the treatments permitted, the owner's financial responsibility, reachable telephone numbers, and whether resuscitation is authorized." },
      { t: "h", x: "Spending Authorization" },
      { t: "p", x: "Please select one:" },
      { t: "check", x: [
        "I authorize emergency veterinary care up to a specified amount without additional approval.",
        "I authorize all treatment reasonably necessary to stabilize or protect my pet until I can be reached.",
        "I authorize examination and stabilization only. Contact me before additional treatment unless delaying treatment would cause unnecessary suffering or create an immediate threat to my pet's life.",
      ] },
      { t: "h", x: "Resuscitation Instructions" },
      { t: "p", x: "Please select one:" },
      { t: "check", x: [
        "Authorize resuscitation: I authorize CPR and other reasonable lifesaving measures.",
        "Do not authorize resuscitation: I do not authorize CPR if my pet experiences cardiac or respiratory arrest.",
      ] },
      { t: "p", x: "I understand that emergency treatment and CPR cannot guarantee recovery." },
      { t: "h", x: "Financial Responsibility" },
      { t: "p", x: "I accept full financial responsibility for veterinary treatment, transportation, medication, supplies, and other emergency expenses involving my pet." },
      { t: "p", x: "Cozy Pet Care may request reimbursement immediately or require payment before the pet is returned." },
      { t: "h", x: "When the Owner Cannot Be Reached" },
      { t: "p", x: "I understand that Cozy Pet Care will make reasonable efforts to reach me or my emergency contact. When delaying treatment could threaten my pet's life or cause substantial suffering, I authorize Cozy Pet Care to act according to the selections above and the veterinarian's professional recommendation." },
      { t: "h", x: "Release" },
      { t: "p", x: "I release Cozy Pet Care and its family members, employees, and authorized helpers from liability for decisions made honestly and reasonably under this authorization, except for gross negligence or intentional misconduct." },
      { t: "h", x: "Signature" },
      { t: "p", x: "I certify that I am at least 18 years old and am the pet's owner or authorized representative." },
      { t: "field", x: "Owner's Printed Name" },
      { t: "field", x: "Owner's Signature" },
      { t: "field", x: "Date" },
    ],
  },
};
